/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AgEvent,
  Autowired,
  Bean,
  ColumnApi,
  Context,
  Events,
  EventService,
  GridApi,
  GridOptionsWrapper,
  GridPanel,
  IRowModel,
  PostConstruct,
  PreDestroy,
  RowBounds,
  RowNode,
} from 'ag-grid-community';
import { forEach, isNil, range } from 'lodash';
import { GridOptionsWrapperExt } from '../ext/grid-options-ext';
import { GridViewport, GridViewportControl, GridViewportRange, GridViewportRowData } from './grid-viewport.interfaces';

@Bean('rowModel')
export class GridViewportRowModel implements IRowModel, GridViewportControl {
  static EVENT_VIEWPORT_RANGE_CHANGED = 'viewportRangeChanged';

  @Autowired('gridOptionsWrapper')
  gridOptionsWrapper: GridOptionsWrapper;
  @Autowired('gridOptionsWrapperExt')
  gridOptionsWrapperExt: GridOptionsWrapperExt;
  @Autowired('context')
  context: Context;
  @Autowired('eventService')
  eventService: EventService;
  @Autowired('gridApi')
  gridApi: GridApi;
  @Autowired('columnApi')
  columnApi: ColumnApi;
  @Autowired('gridPanel')
  gridPanel: GridPanel;

  private rows: { [index: number]: RowNode } = {};
  private rowCount: number = 0;
  private rowHeight: number;
  private _range: GridViewportRange = { start: -1, end: -1 };
  private viewport: GridViewport<any>;
  private initialized = false;

  get range(): GridViewportRange {
    return this._range;
  }

  @PostConstruct // tslint:disable-line:prefer-inline-decorator
  init(): void {
    this.rowHeight = this.gridOptionsWrapper.getRowHeightAsNumber();
    this.eventService.addEventListener(Events.EVENT_VIEWPORT_CHANGED, this.onViewportChanged.bind(this));
    this.eventService.addEventListener(Events.EVENT_GRID_READY, this.onGridReady.bind(this));

    const viewport = this.gridOptionsWrapperExt.getViewport();
    if (viewport) {
      this.setViewport(viewport);
    } else {
      throw new Error('Grid viewport is not defined');
    }
  }

  @PreDestroy // tslint:disable-line:prefer-inline-decorator
  destroy(): void {
    this.viewport.disconnect();
  }

  setViewport(viewport: GridViewport<any>): void {
    if (this.viewport) {
      this.viewport.disconnect();
    }

    this.viewport = viewport;
    this.viewport.connect(this);
  }

  getRow(index: number): RowNode {
    let row = this.rows[index];

    if (isNil(row)) {
      this.rows[index] = row = this.createBlankNode(index);
    }

    return row;
  }

  getPageFirstRow(): number {
    return 0;
  }

  getPageLastRow(): number {
    return this.rowCount - 1;
  }

  getRowCount(): number {
    return this.rowCount;
  }

  getRowIndexAtPixel(pixel: number): number {
    return Math.floor(pixel / this.rowHeight);
  }

  getCurrentPageHeight(): number {
    return this.rowCount * this.rowCount;
  }

  isRowPresent(rowNode: RowNode): boolean {
    return false;
  }

  getRowBounds(index: number): RowBounds {
    const { rowTop, rowHeight } = this.getRow(index);
    return { rowTop, rowHeight };
  }

  isEmpty(): boolean {
    return this.rowCount === 0;
  }

  isRowsToRender(): boolean {
    return this.rowCount > 0;
  }

  getNodesInRangeForSelection(first: RowNode, last: RowNode): RowNode[] {
    const firstIndex = first && first.rowIndex || 0;
    const lastIndex = last.rowIndex;

    if (firstIndex < this._range.start || firstIndex > this._range.end
      || lastIndex < this._range.start || lastIndex > this._range.end) {
      return [];
    }

    return range(firstIndex, lastIndex).map((i) => this.rows[i]);
  }

  forEachNode(callback: (rowNode: RowNode, index: number) => void): void {
    forEach(this.rows, <any>callback);
  }

  getType(): string {
    return 'viewport';
  }

  isLastRowFound(): boolean {
    return true;
  }

  insertRow(index: number, data: any): RowNode {
    const row = this.createBlankNode(index);
    row.setData(data);
    this.shiftRows(1, index + 1);
    return row;
  }

  removeRow(index: number): void {
    const row = this.rows[index];
    if (row) {
      delete this.rows[index];
    }
    this.shiftRows(1, index);
  }

  setRowData(rowData: GridViewportRowData<any>): void {
    forEach(rowData, (data, indexStr) => {
      const index = Number(indexStr);

      if (index >= this._range.start && index <= this._range.end) {
        this.getRow(index).setData(data);
      }
    });
  }

  setRowCount(rowCount: number): void {
    if (rowCount !== this.rowCount) {
      this.rowCount = rowCount;
      this.eventService.dispatchEvent(<AgEvent>{
        type: Events.EVENT_MODEL_UPDATED,
        api: this.gridApi,
        columnApi: this.columnApi,
        newData: false,
        newPage: false,
        keepRenderedRows: false,
        animate: false,
      });
    }
  }

  getRowNode(id: string): RowNode {
    return this.getRow(<any>id);
  }

  private shiftRows(shift: number, startFrom: number = 0): void {
    const newRows: { [index: number]: any } = {};

    forEach(this.rows, (row, indexStr) => {
      const index = Number(indexStr);
      if (index >= startFrom) {
        const newIndex = index + shift;
        row.setId(indexStr);
        row.setRowIndex(newIndex);
        row.setRowTop(this.rowHeight * newIndex);
        newRows[newIndex] = row;
      } else {
        newRows[index] = row;
      }
    });

    this.rows = newRows;
  }

  private createBlankNode(index: number): RowNode {
    const node = new RowNode();
    this.context.wireBean(node);
    node.setId(index.toString());
    node.setRowHeight(this.rowHeight);
    node.setRowTop(this.rowHeight * index);
    node.setRowIndex(index);
    return node;
  }

  private onGridReady(): void {
    this.initialized = true;

    this.onViewportChanged({
      firstRow: 0,
      lastRow: Math.ceil(this.gridPanel.getBodyHeight() / this.gridOptionsWrapper.getRowHeightAsNumber()),
    });
  }

  private onViewportChanged({ firstRow, lastRow }: { firstRow: number; lastRow: number }): void {
    if (!this.initialized || this._range.start < firstRow && lastRow <= this._range.end) {
      return;
    }

    this.cleanRows();

    const rowBuffer = this.gridOptionsWrapper.getRowBuffer();
    const visibleRange = this._range = {
      start: Math.max(0, firstRow - rowBuffer),
      end: lastRow + rowBuffer,
    };

    this.viewport.setVisibleRange(visibleRange);
    this.eventService.dispatchEvent(<AgEvent>{
      type: GridViewportRowModel.EVENT_VIEWPORT_RANGE_CHANGED,
      range: visibleRange,
    });
  }

  private cleanRows(): void {
    forEach(this.rows, (row, indexStr) => {
      const index = Number(indexStr);
      if (index < this._range.start || index > this._range.end) {
        delete this.rows[index];
      }
    });
  }
}
